import { useEffect, useState, useCallback } from "react";
import ProductCard from "../product/ProductCard";
import SkeletonGrid from "./SkeletonGrid";
import { API_BASE, cachedFetch } from "../../api/api";
import { useSEO } from "../../utils/seo";
import "../suits/Category.css";

/**
 * Shared layout for single-collection routes (/suits, /shirts, /shoes,
 * /new-arrivals): compact header + sort control + product grid.
 *
 * Props:
 *   seo              — passed to useSEO()
 *   query            — base query string, e.g. "tag=New" or "category=suits"
 *   heading          — page title shown in the compact header
 *   tagline          — short subtitle (keep category-agnostic)
 *   suppressNewBadge — when true, cards skip the "New" badge (pass on /new-arrivals)
 *   onAddToCart      — forwarded to ProductCard (legacy, kept for compat)
 */
const ProductCollectionPage = ({
  seo,
  query,
  heading,
  tagline,
  suppressNewBadge = false,
  onAddToCart,
}) => {
  useSEO(seo);

  const [products, setProducts] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [sort,     setSort]     = useState("newest");

  const load = useCallback(() => {
    setLoading(true);
    const qs = query ? `${query}&sort=${sort}` : `sort=${sort}`;
    cachedFetch(`${API_BASE}/products?${qs}`)
      .then((data) => { setProducts(data ?? []); setLoading(false); })
      .catch(() => setLoading(false));
  }, [query, sort]);

  useEffect(() => { load(); }, [load]);

  return (
    <main className="category-page">

      <header className="cp-header">
        <div className="cp-header-left">
          <h1 className="cp-title">{heading}</h1>
          <p className="cp-subtitle">{tagline}</p>
        </div>
        <div className="cp-header-right">
          {!loading && (
            <span className="cp-count">{products.length} product{products.length !== 1 ? "s" : ""}</span>
          )}
          <select
            className="cp-sort"
            value={sort}
            onChange={(e) => setSort(e.target.value)}
            aria-label="Sort products"
          >
            <option value="newest">Newest</option>
            <option value="price_asc">Price: Low–High</option>
            <option value="price_desc">Price: High–Low</option>
          </select>
        </div>
      </header>

      <div className="category-products">
        {loading
          ? <SkeletonGrid count={8} className="category-products-inner" />
          : products.map((p, i) => (
              <div key={p.id} className="product-card-anim" style={{ "--i": i }}>
                <ProductCard
                  product={p}
                  onAddToCart={onAddToCart}
                  suppressNewBadge={suppressNewBadge}
                />
              </div>
            ))}
      </div>

    </main>
  );
};

export default ProductCollectionPage;
